// CohortChart — multi-line survival curve chart showing % active by months since join

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { useTheme } from "../../hooks/useTheme";

export interface CohortSeries {
  year: number;
  dataPoints: { months: number; pctActive: number }[];
  // True when the cohort has not yet had ~12 months to be observed, so its
  // later milestones overstate retention (right-censored). Rendered dashed +
  // tagged "(in progress)" so the board doesn't read it as a real retention win.
  censored?: boolean;
}

export interface CohortChartProps {
  cohorts: CohortSeries[];
  isLoading?: boolean;
}

// Distinct color palette for up to 8 cohort years
const COHORT_COLORS = [
  "#2a7a6c", // dcvfd-accent (forest teal)
  "#3b82f6", // blue
  "#f59e0b", // amber
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#84cc16", // lime
  "#f97316", // orange
];

const X_TICKS = [0, 3, 6, 12, 24, 36, 60];

// Playbook target: 50% first-year retention (current ~33%).
const TARGET_RETENTION_PCT = 50;

interface TooltipPayload {
  name: string;
  value: number;
  color: string;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: number;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[140px]">
      <p className="font-semibold text-theme-primary mb-1.5">Month {label}</p>
      {payload.map((entry) => (
        <div
          key={entry.name}
          className="flex items-center justify-between gap-4"
        >
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-theme-muted">{entry.name}</span>
          </span>
          <span
            className="font-bold tabular-nums"
            style={{ color: entry.color }}
          >
            {entry.value != null ? `${entry.value.toFixed(1)}%` : "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function CohortChart({
  cohorts,
  isLoading = false,
}: CohortChartProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton h-3 w-14 rounded-md" />
          ))}
        </div>
        <div className="skeleton rounded-xl" style={{ height: 240 }} />
      </div>
    );
  }

  if (cohorts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-52 gap-2">
        <p className="text-sm text-theme-muted">No cohort data available</p>
        <p className="text-xs text-theme-muted opacity-60">
          Retention data will appear after member records are synced
        </p>
      </div>
    );
  }

  // Build flat data: one object per unique x-tick value, with each cohort year as a key
  // This enables Recharts to plot all lines from a single dataset
  const dataMap = new Map<number, Record<string, number>>();
  for (const tick of X_TICKS) {
    dataMap.set(tick, { months: tick });
  }
  for (const cohort of cohorts) {
    const key = String(cohort.year);
    for (const dp of cohort.dataPoints) {
      const row = dataMap.get(dp.months);
      if (row) {
        row[key] = dp.pctActive;
      }
    }
  }
  const chartData = Array.from(dataMap.values());

  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart
        data={chartData}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="months"
          type="number"
          domain={[0, 60]}
          ticks={X_TICKS}
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          label={{
            value: "Months since join",
            position: "insideBottom",
            offset: -2,
            fontSize: 10,
            fill: axisColor,
          }}
          height={36}
        />
        <YAxis
          domain={[0, 100]}
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => `${v}%`}
          width={40}
        />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine
          y={TARGET_RETENTION_PCT}
          stroke="#2a7a6c"
          strokeDasharray="5 4"
          strokeOpacity={0.7}
          label={{
            value: `Target ${TARGET_RETENTION_PCT}%`,
            position: "insideTopRight",
            fontSize: 10,
            fill: "#2a7a6c",
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, color: axisColor, paddingTop: 8 }}
          iconSize={10}
          iconType="circle"
          formatter={(value: string) => {
            const cohort = cohorts.find((c) => String(c.year) === value);
            return cohort?.censored
              ? `${value} cohort (in progress)`
              : `${value} cohort`;
          }}
        />
        {cohorts.map((cohort, idx) => (
          <Line
            key={cohort.year}
            type="monotone"
            dataKey={String(cohort.year)}
            name={String(cohort.year)}
            stroke={COHORT_COLORS[idx % COHORT_COLORS.length]}
            strokeWidth={2}
            // Right-censored cohorts (not yet 12mo observed) render dashed so an
            // incomplete 2025/26 curve isn't read as a real retention win.
            strokeDasharray={cohort.censored ? "6 4" : undefined}
            strokeOpacity={cohort.censored ? 0.6 : 1}
            dot={{
              r: 3,
              strokeWidth: 0,
              fill: COHORT_COLORS[idx % COHORT_COLORS.length],
            }}
            activeDot={{ r: 4 }}
            connectNulls={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
